import { useCallback, useRef } from 'react';

/**
 * Wraps formik's handleBlur to apply text transforms on blur.
 *
 * @param {object} formik - Formik instance (needs handleBlur, setFieldValue, values)
 * @param {object} fieldTransforms - Map of field name -> transform function
 * @returns {function} Wrapped handleBlur
 *
 * Usage:
 *   const handleBlur = useTextTransformBlur(formik, {
 *     first_name: capitalizeFirst,
 *     address_city: capitalizeWords,
 *     username: toLower,
 *   });
 *
 * @param {object} [options]
 * @param {function(string, *)} [options.onAfterTransform] - Called with (fieldName, effectiveValue)
 *   when the blurred field has a transform; effectiveValue is the transformed string if it
 *   changed, otherwise the previous value.
 */
const useTextTransformBlur = (formik, fieldTransforms, options = {}) => {
  const { onAfterTransform } = options;
  const { handleBlur, setFieldValue, values } = formik;
  const transformsRef = useRef(fieldTransforms);
  transformsRef.current = fieldTransforms;
  const onAfterTransformRef = useRef(onAfterTransform);
  onAfterTransformRef.current = onAfterTransform;

  return useCallback(
    (e) => {
      handleBlur(e);

      const fieldName = e.target?.name || e.target?.id;
      if (!fieldName || !transformsRef.current[fieldName]) return;

      const currentValue = values[fieldName];
      const transformed = transformsRef.current[fieldName](currentValue);
      const effectiveValue = transformed !== currentValue ? transformed : currentValue;

      if (transformed !== currentValue) {
        setFieldValue(fieldName, transformed);
      }
      if (onAfterTransformRef.current) {
        onAfterTransformRef.current(fieldName, effectiveValue);
      }
    },
    [handleBlur, setFieldValue, values],
  );
};

export default useTextTransformBlur;
